import { type FC, type ImgHTMLAttributes } from 'react';

import { buildSrcSet, getImageEntry } from '@shared/lib/responsive-images';

import styles from './Picture.module.css';

export interface PictureProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'alt' | 'srcSet'
> {
  src: string;
  alt: string;
  /** Для главного изображения первого экрана: загрузить сразу с высоким приоритетом. */
  priority?: boolean;
}

// className передаётся картинке и дополняет базовый класс: контейнер задаёт геометрию img,
// в том числе абсолютное позиционирование и участие во flex-раскладке героя.
export const Picture: FC<PictureProps> = ({
  src,
  alt,
  priority = false,
  sizes = '100vw',
  width,
  height,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
  className,
  ...imageProps
}) => {
  const entry = getImageEntry(src);
  const avif = buildSrcSet(entry, 'avif');
  const webp = buildSrcSet(entry, 'webp');

  return (
    <picture className={styles.root}>
      {avif && <source type="image/avif" srcSet={avif} sizes={sizes} />}
      {webp && <source type="image/webp" srcSet={webp} sizes={sizes} />}
      <img
        {...imageProps}
        className={[styles.image, className].filter(Boolean).join(' ')}
        src={src}
        alt={alt}
        srcSet={webp}
        sizes={webp ? sizes : undefined}
        width={entry?.width ?? width}
        height={entry?.height ?? height}
        loading={priority ? 'eager' : loading}
        decoding={decoding}
        fetchPriority={priority ? 'high' : fetchPriority}
      />
    </picture>
  );
};
